import { Helmet } from 'react-helmet';
import { T, useTranslate } from '@tolgee/react';
import { motion } from 'framer-motion';

const PoliticaPrivacidad = () => {
  const { t } = useTranslate();
  
  return (
    <>
      <Helmet>
        <title>{t('legal.privacy.page_title')}</title>
        <meta name="description" content="Política de privacidad de Jarad. Información sobre cómo recopilamos, utilizamos y protegemos tus datos personales." />
      </Helmet>

      <div className="bg-gray-50 py-12">
        <motion.div 
          className="content-container"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="bg-white shadow-sm rounded-lg p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-6">
              <T keyName="legal.privacy.title" />
            </h1>
            
            <div className="prose prose-blue max-w-none">
              <p className="text-gray-600 mb-8">
                <T keyName="legal.privacy.introduction" />
              </p>
              
              <div className="section mb-8">
                <h2 className="text-2xl font-semibold mb-6">
                  <T keyName="legal.privacy.section1.title" />
                </h2>
                
                <div className="overflow-x-auto mb-6">
                  <table className="min-w-full border border-gray-300">
                    <tbody>
                      <tr className="border-b border-gray-300">
                        <th className="bg-gray-50 px-4 py-3 text-left font-semibold border-r border-gray-300">
                          <T keyName="legal.privacy.table.responsible" />
                        </th>
                        <td className="px-4 py-3">VALLE GALBARRO, S.L.</td>
                      </tr>
                      <tr className="border-b border-gray-300">
                        <th className="bg-gray-50 px-4 py-3 text-left font-semibold border-r border-gray-300">
                          <T keyName="legal.privacy.table.purposes" />
                        </th>
                        <td className="px-4 py-3">
                          <ul className="list-disc pl-5">
                            <li><T keyName="legal.privacy.table.purposes.item1" /></li>
                            <li><T keyName="legal.privacy.table.purposes.item2" /></li>
                            <li><T keyName="legal.privacy.table.purposes.item3" /></li>
                          </ul>
                        </td>
                      </tr>
                      <tr className="border-b border-gray-300">
                        <th className="bg-gray-50 px-4 py-3 text-left font-semibold border-r border-gray-300">
                          <T keyName="legal.privacy.table.legitimation" />
                        </th>
                        <td className="px-4 py-3">
                          <ul className="list-disc pl-5">
                            <li><T keyName="legal.privacy.table.legitimation.item1" /></li>
                            <li><T keyName="legal.privacy.table.legitimation.item2" /></li>
                          </ul>
                        </td>
                      </tr>
                      <tr className="border-b border-gray-300">
                        <th className="bg-gray-50 px-4 py-3 text-left font-semibold border-r border-gray-300">
                          <T keyName="legal.privacy.table.recipients" />
                        </th>
                        <td className="px-4 py-3">
                          <T keyName="legal.privacy.table.recipients.description" />
                        </td>
                      </tr>
                      <tr className="border-b border-gray-300">
                        <th className="bg-gray-50 px-4 py-3 text-left font-semibold border-r border-gray-300">
                          <T keyName="legal.privacy.table.retention" />
                        </th>
                        <td className="px-4 py-3">
                          <T keyName="legal.privacy.table.retention.description" />
                        </td>
                      </tr>
                      <tr className="border-b border-gray-300">
                        <th className="bg-gray-50 px-4 py-3 text-left font-semibold border-r border-gray-300">
                          <T keyName="legal.privacy.table.rights" />
                        </th>
                        <td className="px-4 py-3">
                          <T keyName="legal.privacy.table.rights.description" />
                        </td>
                      </tr>
                      <tr className="border-b border-gray-300">
                        <th className="bg-gray-50 px-4 py-3 text-left font-semibold border-r border-gray-300">
                          <T keyName="legal.privacy.table.source" />
                        </th>
                        <td className="px-4 py-3">
                          <T keyName="legal.privacy.table.source.description" />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h3 className="text-xl font-semibold mt-8 mb-4">
                  <T keyName="legal.privacy.controller.title" />
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <p><strong><T keyName="legal.privacy.controller.identity" />:</strong> VALLE GALBARRO, S.L. – NIF: B41286022</p>
                  <p><strong><T keyName="legal.privacy.controller.address" />:</strong> C/ Carretera de Utrera, 19-1 – 41760 El Coronil (Sevilla)</p>
                  <p><strong><T keyName="legal.privacy.controller.phone" />:</strong> 955665041</p>
                  <p><strong><T keyName="legal.privacy.controller.email" />:</strong> <a href="mailto:protecciondatos@vallegalbarro.es" className="text-blue-600 hover:text-blue-800">protecciondatos@vallegalbarro.es</a></p>
                </div>

                <h3 className="text-xl font-semibold mt-8 mb-4">
                  <T keyName="legal.privacy.data_collection.title" />
                </h3>
                <ul className="list-disc pl-5 mb-6">
                  <li><T keyName="legal.privacy.data_collection.item1" /></li>
                  <li><T keyName="legal.privacy.data_collection.item2" /></li>
                  <li><T keyName="legal.privacy.data_collection.item3" /></li>
                  <li><T keyName="legal.privacy.data_collection.item4" /></li>
                </ul>

                <h3 className="text-xl font-semibold mt-8 mb-4">
                  <T keyName="legal.privacy.purposes.title" />
                </h3>
                <ul className="list-disc pl-5 mb-6">
                  <li><T keyName="legal.privacy.purposes.item1" /></li>
                  <li><T keyName="legal.privacy.purposes.item2" /></li>
                  <li><T keyName="legal.privacy.purposes.item3" /></li>
                </ul>

                <h3 className="text-xl font-semibold mt-8 mb-4">
                  <T keyName="legal.privacy.data_categories.title" />
                </h3>
                <ul className="list-disc pl-5 mb-6">
                  <li><strong><T keyName="legal.privacy.data_categories.client" />:</strong> <T keyName="legal.privacy.data_categories.client_data" /></li>
                  <li><strong><T keyName="legal.privacy.data_categories.contact" />:</strong> <T keyName="legal.privacy.data_categories.contact_data" /></li>
                  <li><strong><T keyName="legal.privacy.data_categories.newsletter" />:</strong> <T keyName="legal.privacy.data_categories.newsletter_data" /></li>
                  <li><strong><T keyName="legal.privacy.data_categories.other" />:</strong> <T keyName="legal.privacy.data_categories.other_data" /></li>
                </ul>

                <h3 className="text-xl font-semibold mt-8 mb-4">
                  <T keyName="legal.privacy.data_retention.title" />
                </h3>
                <p className="mb-6">
                  <T keyName="legal.privacy.data_retention.description" />
                </p>

                <h3 className="text-xl font-semibold mt-8 mb-4">
                  <T keyName="legal.privacy.legitimation.title" />
                </h3>
                <ul className="list-disc pl-5 mb-6">
                  <li><strong><T keyName="legal.privacy.legitimation.commercial" />:</strong> <T keyName="legal.privacy.legitimation.commercial_reason" /></li>
                  <li><strong><T keyName="legal.privacy.legitimation.queries" />:</strong> <T keyName="legal.privacy.legitimation.queries_reason" /></li>
                  <li><strong><T keyName="legal.privacy.legitimation.communications" />:</strong> <T keyName="legal.privacy.legitimation.communications_reason" /></li>
                </ul>

                <h3 className="text-xl font-semibold mt-8 mb-4">
                  <T keyName="legal.privacy.recipients.title" />
                </h3>
                <p className="mb-6">
                  <T keyName="legal.privacy.recipients.description" />
                </p>

                <h3 className="text-xl font-semibold mt-8 mb-4">
                  <T keyName="legal.privacy.rights.title" />
                </h3>
                <ul className="list-disc pl-5 mb-6">
                  <li><T keyName="legal.privacy.rights.access" /></li>
                  <li><T keyName="legal.privacy.rights.rectification" /></li>
                  <li><T keyName="legal.privacy.rights.erasure" /></li>
                  <li><T keyName="legal.privacy.rights.limitation" /></li>
                  <li><T keyName="legal.privacy.rights.opposition" /></li>
                  <li><T keyName="legal.privacy.rights.portability" /></li>
                  <li><T keyName="legal.privacy.rights.withdrawal" /></li>
                  <li><T keyName="legal.privacy.rights.complaint" /></li>
                </ul>

                <h4 className="text-lg font-semibold mt-6 mb-3">
                  <T keyName="legal.privacy.rights_exercise.title" />
                </h4>
                <div className="bg-blue-50 p-4 rounded-lg mb-6">
                  <p><strong><T keyName="legal.privacy.rights_exercise.presential" />:</strong> <T keyName="legal.privacy.rights_exercise.presential_description" /></p>
                  <p><strong><T keyName="legal.privacy.rights_exercise.remote" />:</strong> <T keyName="legal.privacy.rights_exercise.remote_description" /></p>
                  <p><T keyName="legal.privacy.rights_exercise.forms" /> <a href="mailto:protecciondatos@vallegalbarro.es" className="text-blue-600 hover:text-blue-800">protecciondatos@vallegalbarro.es</a></p>
                </div>
              </div>

              <div className="section mb-8">
                <h2 className="text-2xl font-semibold mb-6">
                  <T keyName="legal.privacy.section2.title" />
                </h2>
                <p className="mb-4">
                  <T keyName="legal.privacy.section2.description1" />
                </p>
                <p className="mb-4">
                  <T keyName="legal.privacy.section2.description2" />
                </p>
                <p>
                  <T keyName="legal.privacy.section2.description3" />
                </p>
              </div>

              <div className="section mb-8">
                <h2 className="text-2xl font-semibold mb-6">
                  <T keyName="legal.privacy.section3.title" />
                </h2>
                <p>
                  <T keyName="legal.privacy.section3.description" />
                </p>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-8">
                <p className="text-sm text-yellow-800">
                  <strong><T keyName="legal.privacy.note.title" />:</strong> <T keyName="legal.privacy.note.description" />
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default PoliticaPrivacidad; 